import memberLegendStyles from './member-legend.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';

import { terms } from '../../language/terms';
import {
  MEMBERSHIP_EXPLANATIONS,
  MEMBERSHIP_ORDER,
  OPERATING_STATUS_EXPLANATIONS,
  operatingStatusLabel,
} from './shared/company-status-display';
import '../../components/state-badge/agwa-state-badge';

/**
 * Explains the labels on the members list, delivering `@CO-LEGEND-001`.
 *
 * WHY IT EXISTS AT ALL. Until D-036 a company carried one boolean called `Active` that meant
 * "is a member" and controlled nothing. AGWA asked what it did and the honest answer was "nothing
 * anyone expects". The fix splits it into two independent things, and two independent things on one
 * row is precisely the shape that needs explaining - the reader has to know which one stops people
 * working, and no chip can say that by itself.
 *
 * WHY IT SHOWS THE REAL CHIPS rather than describing them. A legend written as prose - "green means
 * a current member" - asks the reader to translate a colour into a word and back. Rendering the same
 * `agwa-state-badge`, resolved through the same state-colour registry the table renders from, lets
 * them match shape to shape. It also makes the two incapable of disagreeing, which prose cannot.
 *
 * WHY THE STATUS ROWS ARE HERE AT ALL. They were deliberately withheld while `Active` gated nothing:
 * a legend entry reading "Blocked - nobody at this company can sign in" would have been a facade,
 * and a legend is the one component on a page whose entire job is to be true. They arrived in the
 * same change that made them true (D-036 stage 2), which is the order this should always go in.
 *
 * Closed by default. It is reference material for the reader who is unsure, not a banner for the
 * reader who is not.
 */
@customElement('member-legend')
export class MemberLegend extends LitElement {
  static styles = unsafeCSS(memberLegendStyles);

  /**
   * The operation-type vocabulary the list's chips speak: AGWA's short code paired
   * with the full name (`@CO-LEGEND-002`). Passed by the host rather than fetched
   * here because the host already fetched the catalogue for the chips themselves -
   * one request, one vocabulary, and the two surfaces cannot disagree. Empty until
   * that fetch lands, and the group simply does not render - ordinary operation,
   * not a fault.
   */
  @property({ attribute: false }) operationTypes: Array<{
    shortCode: string;
    name: string;
  }> = [];

  render() {
    return html`
      <sl-details>
        <!--
          A question mark, and the words phrased as a question. The previous summary read "What
          these labels mean" as a bare statement in a full-width bar, which is the shape of a
          section heading - so it was read as one, and nobody opened it. An icon that means "help"
          plus a question is what says this is something to ask, not something to skim past.
        -->
        <span slot="summary" class="legend-summary">
          <sl-icon name="question-circle"></sl-icon>
          What do these labels mean?
        </span>
        <!--
          Operating status first, because it is the one that governs access and therefore the one a
          reader is most often here to resolve. Membership follows, and the note at the end of it
          says explicitly that it does not do what this group does.
        -->
        <div class="legend-group">
          <h3>Active</h3>
          <div class="legend-rows">
            ${OPERATING_STATUS_EXPLANATIONS.map(
              ({ active, meaning }) => html`
                <agwa-state-badge
                  domain="companyOperating"
                  state=${operatingStatusLabel(active)}
                ></agwa-state-badge>
                <span class="legend-meaning">${meaning}</span>
              `
            )}
          </div>
        </div>

        <div class="legend-group">
          <h3>${terms.membership.value}</h3>
          <div class="legend-rows">
            ${MEMBERSHIP_ORDER.map(
              (status) => html`
                <agwa-state-badge
                  domain="membership"
                  state=${status}
                ></agwa-state-badge>
                <span class="legend-meaning"
                  >${MEMBERSHIP_EXPLANATIONS[status]}</span
                >
              `
            )}
          </div>
          <!--
            The clause the whole legend is for. It is stated rather than implied because the
            confusion this component answers was specifically about which control governs access,
            and an explanation that leaves the reader to infer the answer has not answered it.
          -->
          <p class="legend-note">
            ${terms.membership.value} is a record, not a permission. It does not
            affect whether a ${terms.loginGroup.lower} can use the system.
          </p>
        </div>

        ${
          this.operationTypes.length === 0
            ? nothing
            : html`
                <div class="legend-group">
                  <h3>${terms.userGroupDomain.plural}</h3>
                  <div class="legend-rows">
                    ${this.operationTypes.map(
                      ({ shortCode, name }) => html`
                        <sl-tag size="small" variant="neutral"
                          >${shortCode}</sl-tag
                        >
                        <span class="legend-meaning">${name}</span>
                      `
                    )}
                  </div>
                </div>
              `
        }
      </sl-details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'member-legend': MemberLegend;
  }
}
